import React, { useState, useEffect, useRef } from "react";
import { useProperty } from "../context/PropertyContext";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IP } from '../configuracion.js';
import { API_NOMBREMESA } from './config.js';
import "../styles/ChequeList.css";

// =====================================================
// FUNCIÓN CORREGIDA - MANEJA NULL, UNDEFINED Y NÚMEROS
// =====================================================
const decodeUnicode = (str) => {
  if (str === null || str === undefined) return "";
  if (typeof str !== "string") return String(str);
  if (!str) return "";
  try {
    return str.replace(/\\u[\dA-Fa-f]{4}/g, (match) =>
      String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16))
    );
  } catch (e) {
    console.warn("Error decodificando:", str);
    return String(str);
  }
};

// Función para formatear moneda
const formatCurrency = (amount, numberFormat, currency) => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat(numberFormat || 'en-US', {
    style: "currency",
    currency: currency || 'USD',
  }).format(num);
};

// Función para formatear el tiempo en milisegundos a un string legible
const formatTime = (ms) => {
  if (!ms || ms < 0) return "0 min";
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  } else {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}min`;
  }
};

// Función para obtener el estilo y texto del estado
const getStatusStyle = (estado, isVoided) => {
  if (isVoided) {
    return {
      text: "ANULADO",
      backgroundColor: "#FF5252",
      color: "white",
    };
  }
  switch (estado?.toLowerCase()) {
    case "producido":
      return {
        text: "PRODUCIDO",
        backgroundColor: "#4CAF50",
        color: "white",
      };
    case "pendiente":
      return {
        text: "PENDIENTE",
        backgroundColor: "#FFC107",
        color: "black",
      };
    default:
      return null;
  }
};

// Función para obtener el indicador de pendiente
const getPendingIndicator = (elapsedTime, estado, isVoided) => {
  if (isVoided || estado?.toLowerCase() !== "pendiente" || !elapsedTime) {
    return null;
  }
  const totalMinutes = Math.floor(elapsedTime / 60000);
  return {
    text: formatTime(elapsedTime),
    isOverdue: totalMinutes > 6,
  };
};

// Función para obtener el indicador de rush para PRODUCIDO
const getRushIndicator = (prepTime, estado, isVoided) => {
  if (isVoided || estado?.toLowerCase() !== "producido" || !prepTime) {
    return null;
  }
  const totalMinutes = Math.floor(prepTime / 60000);
  return totalMinutes > 10
    ? { text: formatTime(prepTime), isRush: true }
    : { text: formatTime(prepTime), isRush: false };
};

// Componente para mostrar un modificador de línea
const ModificadorLinea = ({ mod, numberFormat, currency }) => {
  if (!mod) return null;
  
  const precio = Number(mod.precio) || 0;
  const cantidad = Number(mod.cantidad) || 0;
  const consumo = precio * cantidad;
  const descuento = Math.min(Math.max(Number(mod.monto_descuento) || 0, 0), Math.abs(consumo));
  const total = consumo - descuento;
  
  return (
    <div className="modificador-linea">
      <div className="modificador-info">
        <span className="cantidad">{cantidad > 0 ? `+ ${cantidad}x` : `${cantidad}x`}</span>
        <span className="nombre">{decodeUnicode(mod.nombre)}</span>
        {mod.nombre_descuento && (
          <span className="descuento-etiqueta">({decodeUnicode(mod.nombre_descuento)})</span>
        )}
      </div>
      <div className="valores">
        <span className="consumo">{formatCurrency(consumo, numberFormat, currency)}</span>
        <span className="descuento">-{formatCurrency(descuento, numberFormat, currency)}</span>
        <span className="total-modificador">{formatCurrency(total, numberFormat, currency)}</span>
      </div>
    </div>
  );
};

// Componente para mostrar un producto - CON Hooks al inicio
const ProductoItem = ({ producto, fecha_control, numberFormat, currency }) => {
  // TODOS LOS HOOKS DEBEN IR AL INICIO, ANTES DE CUALQUIER RETURN
  const [elapsedTime, setElapsedTime] = useState(
    producto?.estado?.toLowerCase() === "pendiente" && producto?.date_proceso
      ? Date.now() - new Date(producto.date_proceso).getTime()
      : null
  );

  // useEffect para actualizar tiempo de pendiente
  useEffect(() => {
    if (producto?.estado?.toLowerCase() === "pendiente" && !isVoided && producto?.date_proceso) {
      const interval = setInterval(() => {
        setElapsedTime(Date.now() - new Date(producto.date_proceso).getTime());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [producto?.estado, producto?.date_proceso]);

  if (!producto) return null;
  
  const precio = Number(producto.precio) || 0;
  const cantidad = Number(producto.cantidad) || 0;
  const isVoided = (producto.void_itemid !== undefined && producto.void_itemid !== null) || cantidad < 0;
  const statusStyle = getStatusStyle(producto.estado, isVoided);
  
  // Tiempo para "producido" (fijo, basado en datebump)
  const prepTime = producto.estado?.toLowerCase() === "producido" && producto.datebump && producto.date_proceso
    ? new Date(producto.datebump).getTime() - new Date(producto.date_proceso).getTime()
    : null;

  const pendingIndicator = getPendingIndicator(elapsedTime, producto.estado, isVoided);
  const rushIndicator = getRushIndicator(prepTime, producto.estado, isVoided);
  
  const consumo = precio * cantidad;
  const descuento = Math.min(Math.max(Number(producto.monto_descuento) || 0, 0), Math.abs(consumo));
  const totalModificadores = producto.modificadores?.reduce((acc, mod) => {
    if (!mod) return acc;
    const precioMod = Number(mod.precio) || 0;
    const cantidadMod = Number(mod.cantidad) || 0;
    const consumoMod = precioMod * cantidadMod;
    const descuentoMod = Math.min(Math.max(Number(mod.monto_descuento) || 0, 0), Math.abs(consumoMod));
    return acc + (consumoMod - descuentoMod);
  }, 0) || 0;
  const total = consumo - descuento + totalModificadores;

  return (
    <li className={`producto-item ${isVoided ? "voided" : ""}`}>
      <div className="producto-linea">
        <div className="producto-info">
          <span className="cantidad">{isVoided ? `(${Math.abs(cantidad)})x` : `${cantidad}x`}</span>
          <span className={`nombre ${isVoided ? "voided-text" : ""}`}>
            {decodeUnicode(producto.nombre)}
          </span>
          {statusStyle && (
            <span
              className="status-box"
              style={{
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
              }}
            >
              {statusStyle.text}
            </span>
          )}
          {pendingIndicator && (
            <span
              className={`pending-indicator ${pendingIndicator.isOverdue ? "pending-overdue" : ""}`}
              title={pendingIndicator.isOverdue ? "Producto retrasado (> 6 min)" : "Tiempo transcurrido"}
            >
              ⏳ {pendingIndicator.text}
            </span>
          )}
          {rushIndicator && (
            <span
              className={`rush-indicator ${rushIndicator.isRush ? "rush-overdue" : ""}`}
              title={rushIndicator.isRush ? "Preparación lenta (> 10 min)" : "Tiempo de preparación"}
            >
              ⏰ {rushIndicator.text}
            </span>
          )}
          {producto.nombre_descuento && (
            <span className="descuento-etiqueta">({decodeUnicode(producto.nombre_descuento)})</span>
          )}
          {producto.void_itemid && (
            <span className="void-ref">Ref. ID: {producto.void_itemid}</span>
          )}
          {producto.nombre_vendedor && (
            <span className="vendedor-badge">
              <i className="bi bi-person-fill"></i> {decodeUnicode(producto.nombre_vendedor)}
            </span>
          )}
        </div>
        <div className="valores">
          <span className="consumo">{formatCurrency(consumo, numberFormat, currency)}</span>
          {descuento > 0 && (
            <span className="descuento">-{formatCurrency(descuento, numberFormat, currency)}</span>
          )}
          <span className="total">{formatCurrency(total, numberFormat, currency)}</span>
        </div>
      </div>
      {producto.modificadores?.map((mod, index) => (
        <ModificadorLinea key={index} mod={mod} numberFormat={numberFormat} currency={currency} />
      ))}
    </li>
  );
};

// Función para obtener las mesas disponibles desde la API
const fetchMesasDisponibles = async (property) => {
  try {
    console.log("Solicitando mesas para property:", property);
    const response = await fetch(`${API_NOMBREMESA}?property=${encodeURIComponent(property)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Property': property
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error en respuesta:", response.status, errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log("Datos recibidos de mesas:", data);
    
    if (data.success && Array.isArray(data.mesas)) {
      return data.mesas;
    } else if (Array.isArray(data)) {
      return data;
    } else {
      console.warn("Estructura inesperada de datos de mesas:", data);
      return [];
    }
  } catch (error) {
    console.error('Error al obtener las mesas:', error);
    throw error;
  }
};

// Función para actualizar el nombre de la mesa
const actualizarNombreMesa = async (property, numeroCheque, nuevoNombreMesa) => {
  try {
    console.log("Actualizando mesa:", { property, numeroCheque, nuevoNombreMesa });
    
    const requestData = [{
      numeroCheque: numeroCheque,
      nommesa: nuevoNombreMesa
    }];

    console.log("Datos enviados a la API:", JSON.stringify(requestData));
    
    const response = await fetch(API_NOMBREMESA, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Property': property
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error en respuesta:", response.status, errorText);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log("Respuesta de actualización:", data);
    
    if (data.success === false) {
      throw new Error(data.message || "Error al actualizar la mesa");
    }
    
    if (data.results && data.results.length > 0 && data.results[0].success === false) {
      throw new Error(data.results[0].message || "Error en la actualización");
    }
    
    return data;
  } catch (error) {
    console.error('Error al actualizar la mesa:', error);
    throw error;
  }
};

// Componente para mostrar y editar el nombre de la mesa con comentario
const MesaEditor = ({ cheque, onMesaChange, property }) => {
  // TODOS LOS HOOKS AL INICIO
  const [editando, setEditando] = useState(false);
  const [comentario, setComentario] = useState("");
  const [comentariosDisponibles, setComentariosDisponibles] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [errorMesas, setErrorMesas] = useState(null);

  // useEffect para extraer comentario
  useEffect(() => {
    if (!cheque?.mesa) return;
    const nombreCompleto = decodeUnicode(cheque.mesa);
    const partes = nombreCompleto.split(" - ");
    if (partes.length > 1) {
      setComentario(partes.slice(1).join(" - "));
    } else {
      setComentario("");
    }
  }, [cheque?.mesa]);

  // useEffect para cargar comentarios
  useEffect(() => {
    const cargarComentarios = async () => {
      if (!property || !editando) return;
      setCargando(true);
      setErrorMesas(null);
      try {
        const mesas = await fetchMesasDisponibles(property);
        
        const comentariosUnicos = new Set();
        
        mesas.forEach(mesa => {
          if (!mesa) return;
          const nombreDecodificado = decodeUnicode(mesa);
          const partes = nombreDecodificado.split(" - ");
          if (partes.length > 1) {
            comentariosUnicos.add(partes.slice(1).join(" - "));
          }
        });
        
        const comentariosArray = Array.from(comentariosUnicos).sort();
        setComentariosDisponibles(comentariosArray);
        
      } catch (error) {
        console.error("Error cargando mesas:", error);
        setErrorMesas(error.message);
      } finally {
        setCargando(false);
      }
    };
    
    cargarComentarios();
  }, [property, editando]);

  if (!cheque) return null;

  const obtenerNombreBase = (nombreCompleto) => {
    if (!nombreCompleto) return "";
    const nombreDecodificado = decodeUnicode(nombreCompleto);
    const partes = nombreDecodificado.split(" - ");
    return partes[0] || "";
  };

  const nombreBase = obtenerNombreBase(cheque.mesa);

  const manejarCambioModo = () => {
    setEditando(!editando);
    setErrorMesas(null);
  };

  const manejarCambioComentario = (e) => {
    setComentario(e.target.value);
  };

  const manejarSeleccionComentario = (e) => {
    if (e.target.value === "") {
      setComentario("");
    } else if (e.target.value === "escribir") {
      setComentario("");
    } else {
      setComentario(e.target.value);
    }
  };

  const manejarGuardar = async () => {
    if (!cheque?.numero_cheque || !property) return;
    const nuevoNombreCompleto = comentario.trim() 
      ? `${nombreBase} - ${comentario.trim()}`
      : nombreBase;

    setCargando(true);
    try {
      const resultado = await actualizarNombreMesa(property, cheque.numero_cheque, nuevoNombreCompleto);
      console.log("Mesa actualizada:", resultado);
      
      setEditando(false);
      if (onMesaChange) {
        onMesaChange(nuevoNombreCompleto);
      }
      
      alert("¡Mesa actualizada correctamente!");
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error("Error al actualizar mesa:", error);
      alert(`Error al actualizar el nombre de la mesa: ${error.message}`);
    } finally {
      setCargando(false);
    }
  };

  const manejarCancelar = () => {
    setEditando(false);
    setErrorMesas(null);
    if (!cheque?.mesa) return;
    const nombreCompleto = decodeUnicode(cheque.mesa);
    const partes = nombreCompleto.split(" - ");
    if (partes.length > 1) {
      setComentario(partes.slice(1).join(" - "));
    } else {
      setComentario("");
    }
  };

  if (!editando) {
    return (
      <div className="info-item">
        <span className="etiqueta">Mesa:</span>
        <span className="valor">{decodeUnicode(cheque.mesa) || "Sin mesa"}</span>
        <button
          className="btn-editar-mesa"
          onClick={manejarCambioModo}
          title="Añadir o editar comentario de mesa"
          disabled={cargando}
        >
          {cargando ? <i className="bi bi-hourglass-split"></i> : <i className="bi bi-pencil"></i>}
        </button>
      </div>
    );
  }

  return (
    <div className="info-item edicion-mesa">
      <span className="etiqueta">Mesa:</span>
      <div className="contenedor-edicion-mesa">
        {errorMesas ? (
          <div className="error-mesas">
            Error cargando mesas: {errorMesas}
            <button onClick={manejarCancelar}>Cancelar</button>
          </div>
        ) : (
          <>
            <div className="nombre-base">
              <strong>Mesa base:</strong> {nombreBase || "Sin nombre"}
            </div>
            
            <div className="selector-comentario">
              <label htmlFor="selector-comentario">Seleccionar nombre existente:</label>
              <select
                id="selector-comentario"
                className="selector-mesa"
                onChange={manejarSeleccionComentario}
                disabled={cargando}
                value={comentario}
              >
                <option value="">-- Sin nombre adicional --</option>
                <option value="escribir">-- Escribir nuevo nombre --</option>
                {comentariosDisponibles.map((comentario, index) => (
                  <option key={index} value={comentario}>
                    {comentario}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="comentario-container">
              <label htmlFor="comentario-mesa">Nombre adicional:</label>
              <input
                id="comentario-mesa"
                type="text"
                className="input-mesa"
                value={comentario}
                onChange={manejarCambioComentario}
                placeholder="Ej: Eduardo, Juan, Retail, etc."
                disabled={cargando}
              />
            </div>
            
            <div className="vista-previa naranja">
              <strong>Vista previa:</strong> 
              <span>
                {comentario.trim() ? `${nombreBase} - ${comentario.trim()}` : nombreBase}
              </span>
            </div>
            
            <div className="controles-mesa">
              <button 
                className="btn-guardar-mesa"
                onClick={manejarGuardar}
                disabled={cargando}
              >
                {cargando ? 'Guardando...' : <><i className="bi bi-check-lg"></i> Guardar</>}
              </button>
              <button 
                className="btn-cancelar-mesa"
                onClick={manejarCancelar}
                disabled={cargando}
              >
                ✗ Cancelar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Componente para mostrar un cheque
const ChequeCard = ({ cheque, esCerrado, onChequeClick, numberFormat, currency, property }) => {
  if (!cheque) return null;
  
  const calcularTotales = () => {
    let consumoTotal = 0;
    let descuentosTotal = 0;
    
    (cheque.pedidos || []).forEach((pedido) => {
      (pedido.productos || []).forEach((producto) => {
        if (!producto) return;
        const precio = Number(producto.precio) || 0;
        const cantidad = Number(producto.cantidad) || 0;
        const consumo = precio * cantidad;
        const descuento = Math.min(
          Math.max(Number(producto.monto_descuento) || 0, 0),
          Math.abs(consumo)
        );
        consumoTotal += consumo;
        descuentosTotal += descuento;
        
        (producto.modificadores || []).forEach((mod) => {
          if (!mod) return;
          const precioMod = Number(mod.precio) || 0;
          const cantidadMod = Number(mod.cantidad) || 0;
          const consumoMod = precioMod * cantidadMod;
          const descuentoMod = Math.min(
            Math.max(Number(mod.monto_descuento) || 0, 0),
            Math.abs(consumoMod)
          );
          consumoTotal += consumoMod;
          descuentosTotal += descuentoMod;
        });
      });
    });
    return {
      consumoTotal,
      descuentosTotal,
      totalGeneral: consumoTotal - descuentosTotal,
    };
  };

  const { consumoTotal, descuentosTotal, totalGeneral } = calcularTotales();

  return (
    <div
      className={`cheque-card ${esCerrado ? "cerrado" : ""}`}
      onClick={() => !esCerrado && onChequeClick?.(cheque)}
    >
      <div className="cheque-header">
        <div className="cheque-title">
          <h3>
            Cheque #{cheque.numero_cheque || "N/A"}
            {cheque.revenue && (
              <span className="revenue-header-badge">
                {decodeUnicode(cheque.revenue)}
              </span>
            )}
          </h3>
          <span className={`estado ${esCerrado ? "cerrado" : "abierto"}`}>
            {esCerrado
              ? <><i className="bi bi-check-circle-fill"></i> Cerrado</>
              : <><i className="bi bi-pencil-square"></i> Abierto</>}
          </span>
        </div>
        <div className="cheque-details">
          <div className="info-item">
            <span className="etiqueta">Fecha Control:</span>
            <span className="valor">{cheque.fecha_control || "N/A"}</span>
          </div>
          <div className="info-item">
            <span className="etiqueta">Vendedor:</span>
            <span className="valor">{decodeUnicode(cheque.nombre_vendedor) || "N/A"}</span>
          </div>
          {esCerrado && cheque.nombre_cajero && (
            <div className="info-item">
              <span className="etiqueta">Cerrado por:</span>
              <span className="valor">
                <i className="bi bi-person-fill"></i> {decodeUnicode(cheque.nombre_cajero)}
                {cheque.fecha_cierre && (
                  <span className="revenue-detail-badge">
                    {new Date(cheque.fecha_cierre).toLocaleTimeString()}
                  </span>
                )}
              </span>
            </div>
          )}
          <MesaEditor 
            cheque={cheque} 
            onMesaChange={(nuevoNombre) => {
              console.log("Mesa actualizada:", nuevoNombre);
            }}
            property={property}
          />
          <div className="info-item">
            <span className="etiqueta">Cliente:</span>
            <span className="valor">{decodeUnicode(cheque.cliente) || "Mostrador"}</span>
          </div>
          {cheque.revenue && (
            <div className="info-item">
              <span className="etiqueta">Departamento:</span>
              <span className="valor">
                <span className="revenue-detail-badge">
                  {decodeUnicode(cheque.revenue)}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="pedidos-container">
        {(cheque.pedidos || []).map((pedido, idx) => (
          <div key={pedido?.numero_pedido || idx} className="pedido-item">
            <div className="pedido-header">
              <h4>
                Pedido #{pedido?.numero_pedido || "N/A"}
                {pedido?.nombre_vendedor && (
                  <span className="vendedor-pedido-badge">
                    <i className="bi bi-person-fill"></i> {decodeUnicode(pedido.nombre_vendedor)}
                  </span>
                )}
              </h4>
              {pedido?.revenue && (
                <span className="pedido-revenue">
                  {decodeUnicode(pedido.revenue)}
                </span>
              )}
            </div>
            <ul>
              {(pedido?.productos || []).map((producto, idx2) => (
                <ProductoItem
                  key={producto?.id_producto || idx2}
                  producto={producto}
                  fecha_control={cheque.fecha_control}
                  numberFormat={numberFormat}
                  currency={currency}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="cheque-total">
        <div className="total-linea">
          <span className="etiqueta">Consumo total:</span>
          <span className="valor">{formatCurrency(consumoTotal, numberFormat, currency)}</span>
        </div>
        <div className="total-linea">
          <span className="etiqueta">Descuentos totales:</span>
          <span className="valor">-{formatCurrency(descuentosTotal, numberFormat, currency)}</span>
        </div>
        <div className="total-linea general">
          <span className="etiqueta">Total a pagar:</span>
          <span className="valor">{formatCurrency(totalGeneral, numberFormat, currency)}</span>
        </div>
      </div>
    </div>
  );
};

// Componente principal
const ChequeList = ({ onChequeSeleccionado, revenueInicial }) => {
  // TODOS LOS HOOKS AL INICIO, ANTES DE CUALQUIER CONDICIÓN
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("abiertos");
  const [filtroEstadoProducto, setFiltroEstadoProducto] = useState("todos");
  const [filtroRevenue, setFiltroRevenue] = useState(revenueInicial || "todos");
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableRevenues, setAvailableRevenues] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [numberFormat, setNumberFormat] = useState('en-US');
  const [currency, setCurrency] = useState('USD');
  const chequeRefs = useRef({});
  const chequeTabsRef = useRef(null);
  const containerRef = useRef(null); // ref al contenedor del componente

  const { property } = useProperty();

  // useEffect para scroll — usa el contenedor real del POS (.main-content),
  // no window (que tiene overflow:hidden en el POS y nunca genera scrollY)
  useEffect(() => {
    // Encontrar el ancestro scrollable más cercano (main-content del POS)
    const getScrollParent = () => {
      if (!containerRef.current) return window;
      let el = containerRef.current.parentElement;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        if (style.overflow === 'auto' || style.overflow === 'scroll' ||
            style.overflowY === 'auto' || style.overflowY === 'scroll') {
          return el;
        }
        el = el.parentElement;
      }
      return window;
    };

    const scrollParent = getScrollParent();

    const handleScroll = () => {
      const scrollTop = scrollParent === window
        ? window.scrollY
        : scrollParent.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    scrollParent.addEventListener("scroll", handleScroll);
    return () => scrollParent.removeEventListener("scroll", handleScroll);
  }, []);

  // useEffect para cargar datos
  useEffect(() => {
    const cargarConfig = async () => {
      if (!property) {
        console.warn("Propiedad no definida");
        return;
      }
      try {
        const response = await fetch(`${IP}/tavpos/backend/get_timezone.php?property=${encodeURIComponent(property)}`);
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (data.success && data.data) {
          setNumberFormat(data.data.numberFormat || 'en-US');
          setCurrency(data.data.currency || 'USD');
        }
      } catch (error) {
        console.error("Error al cargar configuración:", error);
        setError(error.message);
      }
    };

    const cargarCheques = async () => {
      if (!property) {
        console.warn("Propiedad no definida");
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(
          `${IP}/tavpos/backend/lectura_cheques.php?property=${encodeURIComponent(property)}`
        );
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || "Error en estructura de datos");
        }

        const parseNumber = (value) => {
          if (value === null || value === undefined) return 0;
          if (typeof value === "number") return value;
          const normalized = String(value).replace(/[^0-9.-]/g, "");
          const parsed = parseInt(normalized, 10);
          return isNaN(parsed) ? 0 : parsed;
        };

        const chequesParsed = (data.data || []).map((cheque) => ({
          ...cheque,
          fecha_control: cheque.fecha_control || new Date().toISOString().split("T")[0],
          nombre_vendedor: decodeUnicode(cheque.nombre_vendedor),
          nombre_cajero: decodeUnicode(cheque.nombre_cajero),
          revenue: decodeUnicode(cheque.revenue),
          cliente: decodeUnicode(cheque.cliente),
          mesa: decodeUnicode(cheque.mesa),
          pedidos: (cheque.pedidos || []).map((pedido) => ({
            ...pedido,
            numero_pedido: pedido.numero_pedido,
            nombre_vendedor: decodeUnicode(pedido.nombre_vendedor || cheque.nombre_vendedor),
            revenue: decodeUnicode(pedido.revenue || cheque.revenue),
            productos: (pedido.productos || []).map((producto) => ({
              ...producto,
              nombre: decodeUnicode(producto.nombre),
              nombre_vendedor: decodeUnicode(producto.nombre_vendedor || pedido.nombre_vendedor || cheque.nombre_vendedor),
              nombre_descuento: decodeUnicode(producto.nombre_descuento),
              precio: parseNumber(producto.precio),
              cantidad: parseNumber(producto.cantidad),
              monto_descuento: Math.max(parseNumber(producto.monto_descuento), 0),
              date_proceso: producto.date_proceso || cheque.fecha_control,
              modificadores: (producto.modificadores || []).map((mod) => ({
                ...mod,
                nombre: decodeUnicode(mod.nombre),
                nombre_descuento: decodeUnicode(mod.nombre_descuento),
                precio: parseNumber(mod.precio),
                cantidad: parseNumber(mod.cantidad),
                monto_descuento: Math.max(parseNumber(mod.monto_descuento), 0),
              })),
            })),
          })),
        }));
        setCheques(chequesParsed);

        const uniqueDates = [
          ...new Set(
            chequesParsed
              .map((c) => c.fecha_control)
              .filter((dateStr) => dateStr && typeof dateStr === 'string' && !isNaN(new Date(dateStr).getTime()))
          ),
        ];
        setAvailableDates(uniqueDates);
        
        const uniqueRevenues = [
          ...new Set(
            chequesParsed
              .map((c) => c.revenue)
              .filter((rev) => rev && rev.trim() !== "")
          ),
        ].sort();
        setAvailableRevenues(uniqueRevenues);
        
        if (uniqueDates.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const closestDate = uniqueDates
            .map((dateStr) => ({ str: dateStr, date: new Date(dateStr) }))
            .reduce((prev, curr) =>
              Math.abs(curr.date - today) < Math.abs(prev.date - today) ? curr : prev
            );
          setSelectedDate(closestDate.str);
        }
      } catch (error) {
        console.error("Error cargando cheques:", error);
        setError(error.message || "Error al cargar cheques.");
      } finally {
        setLoading(false);
      }
    };

    if (property) {
      cargarConfig();
      cargarCheques();
    }
  }, [property]);

  // FUNCIÓN PARA FILTRAR PRODUCTOS POR ESTADO
  const filtrarProductosPorEstado = (productos) => {
    if (!productos) return [];
    if (filtroEstadoProducto === "todos") return productos;
    
    if (filtroEstadoProducto === "anulado") {
      return productos.filter(p => 
        p && ((p.void_itemid !== undefined && p.void_itemid !== null) || Number(p.cantidad) < 0)
      );
    }
    
    return productos.filter(p => 
      p && p.estado?.toLowerCase() === filtroEstadoProducto && 
      !((p.void_itemid !== undefined && p.void_itemid !== null) || Number(p.cantidad) < 0)
    );
  };

  // FUNCIÓN PARA FILTRAR CHEQUES POR DEPARTAMENTO (REVENUE)
  const filtrarChequesPorRevenue = (chequesList) => {
    if (!chequesList) return [];
    if (filtroRevenue === "todos") return chequesList;
    return chequesList.filter(cheque => 
      cheque && cheque.revenue && decodeUnicode(cheque.revenue) === filtroRevenue
    );
  };

  const calcularTotalesFiltrados = (chequesFiltrados) => {
    let consumoTotal = 0;
    let descuentosTotal = 0;
    
    (chequesFiltrados || []).forEach((cheque) => {
      (cheque.pedidos || []).forEach((pedido) => {
        const productosFiltrados = filtrarProductosPorEstado(pedido.productos || []);
        
        productosFiltrados.forEach((producto) => {
          if (!producto) return;
          const precio = Number(producto.precio) || 0;
          const cantidad = Number(producto.cantidad) || 0;
          const consumo = precio * cantidad;
          const descuento = Math.min(
            Math.max(Number(producto.monto_descuento) || 0, 0),
            Math.abs(consumo)
          );
          consumoTotal += consumo;
          descuentosTotal += descuento;
          
          (producto.modificadores || []).forEach((mod) => {
            if (!mod) return;
            const precioMod = Number(mod.precio) || 0;
            const cantidadMod = Number(mod.cantidad) || 0;
            const consumoMod = precioMod * cantidadMod;
            const descuentoMod = Math.min(
              Math.max(Number(mod.monto_descuento) || 0, 0),
              Math.abs(consumoMod)
            );
            consumoTotal += consumoMod;
            descuentosTotal += descuentoMod;
          });
        });
      });
    });
    
    return {
      consumoTotal: formatCurrency(consumoTotal, numberFormat, currency),
      descuentosTotal: formatCurrency(descuentosTotal, numberFormat, currency),
      totalGeneral: formatCurrency(consumoTotal - descuentosTotal, numberFormat, currency),
    };
  };

  const calcularContadoresEstado = (chequesFiltrados) => {
    let pendientes = 0;
    let producidos = 0;
    let anulados = 0;
    
    (chequesFiltrados || []).forEach((cheque) => {
      (cheque.pedidos || []).forEach((pedido) => {
        (pedido.productos || []).forEach((producto) => {
          if (!producto) return;
          const isVoided = (producto.void_itemid !== undefined && producto.void_itemid !== null) || Number(producto.cantidad) < 0;
          
          if (isVoided) {
            anulados++;
          } else if (producto.estado?.toLowerCase() === "pendiente") {
            pendientes++;
          } else if (producto.estado?.toLowerCase() === "producido") {
            producidos++;
          }
        });
      });
    });
    
    return { pendientes, producidos, anulados };
  };

  const calcularContadoresRevenue = (chequesFiltrados) => {
    const contadores = {};
    (chequesFiltrados || []).forEach((cheque) => {
      if (!cheque) return;
      const revenue = decodeUnicode(cheque.revenue) || "Sin departamento";
      contadores[revenue] = (contadores[revenue] || 0) + 1;
    });
    return contadores;
  };

  const scrollLeft = () => {
    if (chequeTabsRef.current) {
      chequeTabsRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };
  
  const scrollRight = () => {
    if (chequeTabsRef.current) {
      chequeTabsRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };
  
  const scrollToTop = () => {
    // Usar el mismo contenedor scrollable del POS
    if (!containerRef.current) return;
    let el = containerRef.current.parentElement;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      if (style.overflow === 'auto' || style.overflow === 'scroll' ||
          style.overflowY === 'auto' || style.overflowY === 'scroll') {
        el.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      el = el.parentElement;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDateTabClick = (dateStr) => {
    setSelectedDate(dateStr);
    setFiltro("todos");
  };

  const scrollToCheque = (numeroCheque) => {
    if (chequeRefs.current[numeroCheque]) {
      chequeRefs.current[numeroCheque].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const parseDateString = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  // APLICAR FILTROS
  const chequesFiltradosPorFecha = selectedDate
    ? (cheques || []).filter((cheque) => cheque && cheque.fecha_control === selectedDate)
    : cheques || [];

  const chequesFiltradosPorRevenue = filtrarChequesPorRevenue(chequesFiltradosPorFecha);

  const chequesFiltradosPorEstadoCheque =
    filtro === "abiertos"
      ? (chequesFiltradosPorRevenue || []).filter((cheque) => cheque && !cheque.fecha_cierre)
      : filtro === "cerrados"
      ? (chequesFiltradosPorRevenue || []).filter((cheque) => cheque && cheque.fecha_cierre)
      : chequesFiltradosPorRevenue || [];

  const chequesConProductosFiltrados = (chequesFiltradosPorEstadoCheque || [])
    .map((cheque) => ({
      ...cheque,
      pedidos: (cheque.pedidos || [])
        .map((pedido) => ({
          ...pedido,
          productos: filtrarProductosPorEstado(pedido.productos || [])
        }))
        .filter(pedido => pedido.productos && pedido.productos.length > 0)
    }))
    .filter(cheque => cheque.pedidos && cheque.pedidos.length > 0);

  const { consumoTotal, descuentosTotal, totalGeneral } =
    calcularTotalesFiltrados(chequesConProductosFiltrados);

  const totalCheques = chequesFiltradosPorRevenue.length;
  const totalChequesAbiertos = (chequesFiltradosPorRevenue || []).filter(
    (c) => c && !c.fecha_cierre
  ).length;
  const totalChequesCerrados = (chequesFiltradosPorRevenue || []).filter(
    (c) => c && c.fecha_cierre
  ).length;

  const contadoresEstado = calcularContadoresEstado(chequesFiltradosPorRevenue);
  const contadoresRevenue = calcularContadoresRevenue(chequesFiltradosPorRevenue);

  if (loading) return <div className="cargando">Cargando cheques...</div>;
  if (error) return <div className="error">⚠️ Error: {error}</div>;

  return (
    <div className="cheque-list" ref={containerRef}>
      <header>
        <h2>Monitor de Producción</h2>
        
        {/* BARRA DE FECHAS */}
        <div className="date-container">
          <div className="date-tabs-container">
            {availableDates
              .slice()
              .sort((a, b) => new Date(b) - new Date(a))
              .map((dateStr) => (
                <button
                  key={dateStr}
                  className={selectedDate && dateStr === selectedDate ? "active" : ""}
                  onClick={() => handleDateTabClick(dateStr)}
                >
                  {dateStr}
                </button>
              ))}
            <div className="filtro-item date-picker-container">
              <label></label>
              <DatePicker
                selected={selectedDate ? parseDateString(selectedDate) : null}
                onChange={(date) => {
                  if (date) {
                    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    handleDateTabClick(dateStr);
                  } else {
                    handleDateTabClick(null);
                  }
                }}
                includeDates={availableDates.map((dateStr) => parseDateString(dateStr))}
                dateFormat="yyyy-MM-dd"
                placeholderText="Selecciona una fecha"
                className="date-picker-input"
              />
            </div>
          </div>
        </div>

        {/* FILTROS PRINCIPALES */}
        <div className="filtros-principales">
          {/* FILTRO ESTADO CHEQUE */}
          <div className="filtro-cheque">
            <button
              className={filtro === "todos" ? "active" : ""}
              onClick={() => setFiltro("todos")}
            >
              Todos <span className="contador">{totalCheques}</span>
            </button>
            <button
              className={filtro === "abiertos" ? "active" : ""}
              onClick={() => setFiltro("abiertos")}
            >
              Abiertos <span className="contador">{totalChequesAbiertos}</span>
            </button>
            <button
              className={filtro === "cerrados" ? "active" : ""}
              onClick={() => setFiltro("cerrados")}
            >
              Cerrados <span className="contador">{totalChequesCerrados}</span>
            </button>
          </div>

          {/* FILTRO DEPARTAMENTO (REVENUE) — oculto: se aplica automáticamente desde el revenue activo */}

          {/* FILTRO ESTADO PRODUCTO */}
          <div className="filtro-estado">
            <button 
              data-estado="todos"
              className={filtroEstadoProducto === "todos" ? "active" : ""}
              onClick={() => setFiltroEstadoProducto("todos")}
            >
              Todos <span className="contador">{contadoresEstado.pendientes + contadoresEstado.producidos + contadoresEstado.anulados}</span>
            </button>
            <button 
              data-estado="pendiente"
              className={filtroEstadoProducto === "pendiente" ? "active" : ""}
              onClick={() => setFiltroEstadoProducto("pendiente")}
            >
              Pendiente <span className="contador">{contadoresEstado.pendientes}</span>
            </button>
            <button 
              data-estado="producido"
              className={filtroEstadoProducto === "producido" ? "active" : ""}
              onClick={() => setFiltroEstadoProducto("producido")}
            >
              Producido <span className="contador">{contadoresEstado.producidos}</span>
            </button>
            <button 
              data-estado="anulado"
              className={filtroEstadoProducto === "anulado" ? "active" : ""}
              onClick={() => setFiltroEstadoProducto("anulado")}
            >
              Anulado <span className="contador">{contadoresEstado.anulados}</span>
            </button>
          </div>
        </div>

        {/* TOTALES GLOBALES */}
        <div className="totales-globales">
          <div className="total-linea">
            <span className="etiqueta">Consumo total:</span>
            <span className="valor">{consumoTotal}</span>
          </div>
          <div className="total-linea">
            <span className="etiqueta">Descuentos totales:</span>
            <span className="valor">-{descuentosTotal}</span>
          </div>
          <div className="total-linea general">
            <span className="etiqueta">Total a pagar:</span>
            <span className="valor">{totalGeneral}</span>
          </div>
        </div>

        {/* TABS DE CHEQUES */}
        <div className="cheque-tabs-wrapper">
          <button className="scroll-btn left" onClick={scrollLeft}>←</button>
          <div className="cheque-tabs-container" ref={chequeTabsRef}>
            {chequesConProductosFiltrados.length > 0 ? (
              chequesConProductosFiltrados.map((cheque) => (
                <button
                  key={cheque.numero_cheque}
                  className="cheque-tab"
                  onClick={() => scrollToCheque(cheque.numero_cheque)}
                >
                  <span className="numero-cheque">#{cheque.numero_cheque}</span>
                  <span className="mesa">{decodeUnicode(cheque.mesa) || "Sin mesa"}</span>
                  {cheque.revenue && (
                    <span className="revenue-badge">{cheque.revenue}</span>
                  )}
                </button>
              ))
            ) : (
              <div className="sin-tabs">No hay cheques disponibles</div>
            )}
          </div>
          <button className="scroll-btn right" onClick={scrollRight}>→</button>
        </div>
      </header>

      {/* LISTA DE CHEQUES FILTRADOS */}
      <section className="cheques">
        {chequesConProductosFiltrados.length === 0 ? (
          <div className="sin-cheques">🎉 ¡No hay cheques para mostrar!</div>
        ) : (
          chequesConProductosFiltrados.map((cheque) => (
            <div
              key={cheque.numero_cheque}
              ref={(el) => (chequeRefs.current[cheque.numero_cheque] = el)}
            >
              <ChequeCard
                cheque={cheque}
                esCerrado={!!cheque.fecha_cierre}
                onChequeClick={onChequeSeleccionado}
                numberFormat={numberFormat}
                currency={currency}
                property={property}
              />
            </div>
          ))
        )}
      </section>

      {/* BOTÓN VOLVER ARRIBA */}
      {showScrollTop && (
        <button className="scroll-top-btn" onClick={scrollToTop} title="Volver arriba">
          <i className="bi bi-arrow-up"></i>
        </button>
      )}
    </div>
  );
};

ChequeList.propTypes = {
  onChequeSeleccionado: PropTypes.func.isRequired,
};

ChequeCard.propTypes = {
  cheque: PropTypes.object,
  esCerrado: PropTypes.bool,
  onChequeClick: PropTypes.func,
  numberFormat: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
};

ModificadorLinea.propTypes = {
  mod: PropTypes.object,
  numberFormat: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};

ProductoItem.propTypes = {
  producto: PropTypes.object,
  fecha_control: PropTypes.string.isRequired,
  numberFormat: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};

MesaEditor.propTypes = {
  cheque: PropTypes.object,
  onMesaChange: PropTypes.func,
  property: PropTypes.string.isRequired,
};

export default ChequeList;